import type { GitpodAPI } from "@/api";
import type { EventInterest } from "@/hooks/use-event-interest";
import { reconnectDelay } from "@/utils/reconnect-backoff";
import type { QueryClient } from "@tanstack/react-query";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    ResourceType,
    WatchEventsRequest_ResourceTypeFilter,
    type WatchEventsResponse,
} from "gitpod-next-api/gitpod/v1/event_pb";

/** Check if two sets contain exactly the same elements.
 * note(ft): I wish I could use Set.prototype.symmetricDifference, but although it has evergreen support, we've an enteprise business and our customers sometimes have crazy outdated browsers (we've seen this when we tried using URL.canParse).
 */
function setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const v of a) {
        if (!b.has(v)) return false;
    }
    return true;
}

/** How long to wait after an interest change before reconnecting the stream. */
const FILTER_DEBOUNCE_MS = 500;

/**
 * Manages a single org-scoped WatchEvents stream whose filters reflect
 * the union of all active interests registered via useEventInterest.
 *
 * When interests change, the manager debounces for FILTER_DEBOUNCE_MS,
 * computes new filters, and reconnects the stream. The old stream is
 * kept open until the new one delivers its first message (overlapping
 * reconnection) to avoid missing events during the transition.
 */
export class OrgEventStreamManager {
    private interests = new Map<string, EventInterest>();
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private currentAbort: AbortController | null = null;
    private disposed = false;
    private reconnectAttempt = 0;

    private _currentFilters: WatchEventsRequest_ResourceTypeFilter[] = [];

    /** The filters currently applied to the active stream. */
    get currentFilters(): readonly WatchEventsRequest_ResourceTypeFilter[] {
        return this._currentFilters;
    }

    constructor(
        private api: typeof GitpodAPI,
        private client: QueryClient,
        private onEvent: (api: typeof GitpodAPI, client: QueryClient, response: WatchEventsResponse) => Promise<void>,
    ) {}

    addInterest(id: string, interest: EventInterest) {
        this.interests.set(id, interest);
        this.scheduleReconnect();
    }

    removeInterest(id: string) {
        this.interests.delete(id);
        this.scheduleReconnect();
    }

    /** Compute ResourceTypeFilter[] from the union of all active interests. */
    computeFilters(): WatchEventsRequest_ResourceTypeFilter[] {
        // Group interests by resource type. null means wildcard (no filter).
        // Once null, stays null — a wildcard interest overrides all specific IDs.
        const byType = new Map<ResourceType, { resourceIds: Set<string> | null; creatorIds: Set<string> | null }>();

        for (const interest of this.interests.values()) {
            let entry = byType.get(interest.resourceType);
            if (!entry) {
                entry = { resourceIds: new Set(), creatorIds: new Set() };
                byType.set(interest.resourceType, entry);
            }

            if (entry.resourceIds !== null) {
                if (!interest.resourceIds || interest.resourceIds.length === 0) {
                    entry.resourceIds = null;
                } else {
                    for (const id of interest.resourceIds) {
                        entry.resourceIds.add(id);
                    }
                }
            }

            if (entry.creatorIds !== null) {
                if (!interest.creatorIds || interest.creatorIds.length === 0) {
                    entry.creatorIds = null;
                } else {
                    for (const id of interest.creatorIds) {
                        entry.creatorIds.add(id);
                    }
                }
            }
        }

        const filters: WatchEventsRequest_ResourceTypeFilter[] = [];
        for (const [resourceType, entry] of byType) {
            filters.push(
                new WatchEventsRequest_ResourceTypeFilter({
                    resourceType,
                    resourceIds: entry.resourceIds ? [...entry.resourceIds] : [],
                    creatorIds: entry.creatorIds ? [...entry.creatorIds] : [],
                }),
            );
        }

        return filters;
    }

    /** Start the stream with current filters. Called on first interest and after debounce. */
    private async connect(filters: WatchEventsRequest_ResourceTypeFilter[]) {
        // Close the previous stream immediately before opening the new one.
        // The 500ms debounce already absorbs mount/unmount churn, and React Query
        // will refetch stale data on reconnect, so a brief gap is acceptable.
        if (this.currentAbort) {
            this.currentAbort.abort("Replaced by new stream");
        }

        const abort = new AbortController();
        this.currentAbort = abort;
        this._currentFilters = filters;

        try {
            console.debug("[OrgEventStreamManager] Connecting with", filters.length, "filter(s)");

            for await (const response of this.api.eventService.watchEvents(
                {
                    scope: { case: "organization", value: true },
                    resourceTypeFilters: filters,
                },
                {
                    signal: abort.signal,
                    timeoutMs: -1,
                },
            )) {
                if (abort.signal.aborted) {
                    return;
                }

                // Reset backoff on successful message delivery
                this.reconnectAttempt = 0;

                // Skip keepalive messages
                if (response.resourceType === ResourceType.UNSPECIFIED) {
                    continue;
                }

                try {
                    await this.onEvent(this.api, this.client, response);
                } catch (err) {
                    console.error("[OrgEventStreamManager] Event handler error:", err);
                }
            }
        } catch (err) {
            if (abort.signal.aborted) {
                return;
            }
            // Match legacy behavior: stop retrying on terminal errors.
            if (err instanceof ConnectError) {
                if (
                    err.code === Code.Canceled ||
                    err.code === Code.NotFound ||
                    err.code === Code.PermissionDenied ||
                    err.code === Code.Unauthenticated ||
                    err.code === Code.Internal
                ) {
                    console.debug("[OrgEventStreamManager] Terminal error, not reconnecting:", err.code);
                    return;
                }
            }
            console.error("[OrgEventStreamManager] Stream error:", err);
            if (!this.disposed) {
                const delay = reconnectDelay(this.reconnectAttempt);
                this.reconnectAttempt++;
                console.debug(
                    "[OrgEventStreamManager] Reconnecting in",
                    Math.round(delay),
                    "ms (attempt",
                    this.reconnectAttempt,
                    ")",
                );
                setTimeout(() => {
                    if (!this.disposed) {
                        void this.connect(this._currentFilters);
                    }
                }, delay);
            }
        }
    }

    private scheduleReconnect() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        this.debounceTimer = setTimeout(() => {
            this.debounceTimer = null;

            const filters = this.computeFilters();

            // If no interests remain, tear down the stream.
            if (filters.length === 0) {
                if (this.currentAbort) {
                    console.debug("[OrgEventStreamManager] No interests, closing stream");
                    this.currentAbort.abort("No interests");
                    this.currentAbort = null;
                    this._currentFilters = [];
                }
                return;
            }

            // Skip reconnect if filters haven't changed.
            if (filtersEqual(this._currentFilters, filters)) {
                return;
            }

            void this.connect(filters);
        }, FILTER_DEBOUNCE_MS);
    }

    dispose() {
        this.disposed = true;
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        if (this.currentAbort) {
            this.currentAbort.abort("Disposed");
            this.currentAbort = null;
        }
        this.interests.clear();
        this._currentFilters = [];
    }
}

function filterKey(f: WatchEventsRequest_ResourceTypeFilter): string {
    return `${f.resourceType}:r=${[...f.resourceIds].sort().join(",")}:c=${[...f.creatorIds].sort().join(",")}`;
}

function filtersEqual(a: WatchEventsRequest_ResourceTypeFilter[], b: WatchEventsRequest_ResourceTypeFilter[]): boolean {
    const keysA = new Set(a.map(filterKey));
    const keysB = new Set(b.map(filterKey));
    return setsEqual(keysA, keysB);
}
